import {
  Badge,
  Box,
  Flex,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { CSSProperties, useEffect, useRef, useState } from "react";
import {
  CategoryTreeSelect,
  matchCategory,
  useCategoryImage,
  useCategoryTree,
} from "../CategoryTreeSelect";
import {
  DragHandle,
  reorderList,
  SectionCard,
  SortableArea,
  useSortableItem,
  ZoomableImage,
} from "../HomeBlockShared";
import { TileItem, TilesConfig, TilesLayout, TileSideCategory } from "../homeBlockConfig";
import { HomeBlock } from "../homeBlockData";
import classes from "../homeBlock.module.css";
import {
  Crop,
  EditableList,
  fieldSetter,
  IconPicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

type LayoutCell = {
  // Ô lớn: có thêm nhãn nhỏ, tiêu đề tô màu, điểm nổi bật
  big?: boolean;
  // Ghi chú hình dạng ô hiện cạnh số ô
  tag?: string;
  style?: CSSProperties;
};

type LayoutOption = {
  key: TilesLayout;
  name: string;
  columns: string;
  rows: string;
  // Thứ tự ô = thứ tự trong config.tiles
  cells: LayoutCell[];
  // Có khối danh mục phụ bên phải (chỉ để vẽ sơ đồ)
  panel?: boolean;
};

const at = (column: string, row: string): CSSProperties => ({ gridColumn: column, gridRow: row });

// Bố cục cố định theo thiết kế của từng block; tỉ lệ cột/hàng lấy theo kích thước ô trong ảnh thiết kế
const LAYOUTS: LayoutOption[] = [
  {
    key: "big-left",
    name: "Ô lớn bên trái + 6 ô",
    columns: "522fr 222fr 227fr 232fr",
    rows: "1fr 1fr",
    cells: [
      { big: true, tag: "lớn", style: at("1", "1 / span 2") },
      { style: at("2", "1") },
      { style: at("3", "1") },
      { style: at("4", "1") },
      { style: at("2", "2") },
      { style: at("3", "2") },
      { style: at("4", "2") },
    ],
  },
  {
    key: "big-center",
    name: "Ô lớn ở giữa + 8 ô",
    columns: "185fr 186fr 461fr 186fr 185fr",
    rows: "1fr 1fr",
    cells: [
      { big: true, tag: "lớn", style: at("3", "1 / span 2") },
      { style: at("1", "1") },
      { style: at("2", "1") },
      { style: at("1", "2") },
      { style: at("2", "2") },
      { style: at("4", "1") },
      { style: at("5", "1") },
      { style: at("4", "2") },
      { style: at("5", "2") },
    ],
  },
  {
    key: "tall-left",
    name: "Ô cao bên trái + 7 ô",
    columns: "287fr 256fr 223fr 115fr 110fr 203fr",
    rows: "1fr 1fr",
    cells: [
      { tag: "cao", style: at("1", "1 / span 2") },
      { style: at("2", "1") },
      { style: at("3", "1") },
      { style: at("4 / span 2", "1") },
      { style: at("6", "1") },
      { style: at("2", "2") },
      { tag: "rộng", style: at("3 / span 2", "2") },
      { tag: "rộng", style: at("5 / span 2", "2") },
    ],
  },
  {
    key: "wide-tall",
    name: "Ô ngang bên trái, ô cao ở giữa + 6 ô",
    columns: "251fr 208fr 239fr 249fr 239fr",
    rows: "1fr 1fr",
    cells: [
      { tag: "ngang", style: at("1 / span 2", "1") },
      { tag: "cao", style: at("3", "1 / span 2") },
      { style: at("4", "1") },
      { style: at("5", "1") },
      { style: at("1", "2") },
      { style: at("2", "2") },
      { style: at("4", "2") },
      { style: at("5", "2") },
    ],
  },
  {
    key: "banner-top",
    name: "Banner trên + 6 ô, khối danh mục phụ bên phải",
    columns: "227fr 227fr 220fr",
    rows: "280fr 150fr 154fr",
    cells: [
      { big: true, tag: "banner", style: at("1 / span 3", "1") },
      { style: at("1", "2") },
      { style: at("2", "2") },
      { style: at("3", "2") },
      { style: at("1", "3") },
      { style: at("2", "3") },
      { style: at("3", "3") },
    ],
    panel: true,
  },
  {
    key: "grid-4",
    name: "Lưới đều 4 cột, 8 ô",
    columns: "repeat(4, minmax(0, 1fr))",
    rows: "1fr 1fr",
    cells: Array.from({ length: 8 }, () => ({})),
  },
];

const layoutOf = (key: TilesLayout) => LAYOUTS.find((l) => l.key === key) ?? LAYOUTS[0];

const slotLabel = (cell: LayoutCell, index: number) =>
  cell.tag ? `Ô ${index + 1} · ${cell.tag}` : `Ô ${index + 1}`;

type SampleTile = Pick<TileItem, "id" | "title" | "description"> &
  Partial<Pick<TileItem, "highlight" | "titleEnd" | "eyebrow" | "buttonLabel">> & {
    category: string;
    crop: Crop;
    features?: [string, string, string][];
  };

const st = (
  id: string,
  category: string,
  title: string,
  description: string,
  crop: Crop,
  extra?: Partial<SampleTile>
): SampleTile => ({ id, category, title, description, crop, ...extra });

// Dữ liệu mẫu theo thiết kế. Ảnh cắt từ thiết kế chỉ để xem khi chưa có ảnh, không lưu.
const SAMPLE_TILES: Record<string, SampleTile[]> = {
  "home-gaming-gear": [
    st("t-big", "Gaming Gear", "NÂNG TẦM", "Trang bị gaming gear chính hãng hiệu năng cao, sẵn sàng cho mọi trận chiến", { x: 25, y: 60, w: 522, h: 475 }, { highlight: "TRẢI NGHIỆM GAME", eyebrow: "GAMING GEAR", buttonLabel: "Khám phá ngay" }),
    st("t-mouse", "Chuột Gaming", "Chuột Gaming", "Độ chính xác cao\nPhản hồi nhanh", { x: 557, y: 60, w: 222, h: 228 }),
    st("t-keyboard", "Bàn phím Gaming", "Bàn phím Gaming", "Cảm giác gõ đã tay\nTùy biến linh hoạt", { x: 790, y: 60, w: 227, h: 228 }),
    st("t-headset", "Tai nghe Gaming", "Tai nghe Gaming", "Âm thanh sống động\nTập trung mọi trận đấu", { x: 1027, y: 60, w: 232, h: 228 }),
    st("t-mic", "Micro Gaming", "Micro Gaming", "Thu âm rõ nét\nGiao tiếp mượt mà", { x: 557, y: 297, w: 222, h: 238 }),
    st("t-chair", "Ghế Gaming", "Ghế Gaming", "Thiết kế công thái học\nThoải mái giành chiến thắng", { x: 790, y: 297, w: 227, h: 238 }),
    st("t-accessory", "Phụ kiện Gaming", "Phụ kiện Gaming", "Lót chuột, tay cầm, giá treo, cáp, hub...", { x: 1027, y: 297, w: 232, h: 238 }),
  ],
  "home-pc-parts": [
    st("pc-case", "Vỏ case", "KIẾN TẠO", "", { x: 410, y: 67, w: 461, h: 356 }, {
      highlight: "PHONG CÁCH",
      titleEnd: "RIÊNG CỦA BẠN",
      eyebrow: "VỎ CASE",
      buttonLabel: "Khám phá ngay",
      features: [
        ["palette", "Thiết kế hiện đại", "Đa dạng phong cách"],
        ["fan", "Tối ưu tản nhiệt", "Hiệu suất cao"],
        ["cpu", "Tương thích linh kiện", "Dễ dàng nâng cấp"],
      ],
    }),
    st("pc-cpu", "CPU", "CPU", "Intel | AMD", { x: 20, y: 67, w: 185, h: 176 }),
    st("pc-main", "Mainboard", "Mainboard", "ASUS | MSI | GIGABYTE", { x: 214, y: 67, w: 186, h: 176 }),
    st("pc-vga", "VGA", "VGA - Card đồ họa", "NVIDIA | AMD | Intel", { x: 20, y: 252, w: 185, h: 171 }),
    st("pc-ram", "RAM", "RAM", "DDR4 | DDR5", { x: 214, y: 252, w: 186, h: 171 }),
    st("pc-ssd", "SSD", "SSD", "NVMe | SATA", { x: 880, y: 67, w: 186, h: 176 }),
    st("pc-hdd", "HDD", "HDD", "3.5\" | 2.5\"", { x: 1075, y: 67, w: 185, h: 176 }),
    st("pc-psu", "Nguồn máy tính", "PSU - Nguồn", "80 Plus | Modular", { x: 880, y: 252, w: 186, h: 171 }),
    st("pc-cooler", "Tản nhiệt", "Tản nhiệt", "Tản khí | Tản nước | Fan", { x: 1075, y: 252, w: 185, h: 171 }),
  ],
  "home-console": [
    st("cs-ps", "PlayStation", "PlayStation", "Trải nghiệm game đỉnh cao cùng PlayStation", { x: 26, y: 119, w: 287, h: 490 }),
    st("cs-xbox", "Xbox", "Xbox", "Hiệu năng mạnh mẽ cho mọi trải nghiệm", { x: 322, y: 119, w: 256, h: 238 }),
    st("cs-steam", "Steam Deck", "Steam Deck", "Thế giới game PC trong lòng bàn tay", { x: 586, y: 119, w: 223, h: 266 }),
    st("cs-sim", "Sim Racing", "Sim Racing", "Trải nghiệm đua xe chân thực", { x: 818, y: 119, w: 225, h: 266 }),
    st("cs-vr", "Kính thực tế ảo", "VR", "Khám phá thế giới thực tế ảo", { x: 1052, y: 119, w: 203, h: 266 }),
    st("cs-nintendo", "Nintendo", "Nintendo", "Giải trí mọi lúc mọi nơi", { x: 322, y: 366, w: 256, h: 243 }),
    st("cs-pad", "Tay cầm", "Tay Cầm", "Đa dạng tay cầm cho mọi nền tảng", { x: 586, y: 394, w: 338, h: 215 }),
    st("cs-acc", "Phụ kiện Console", "Phụ Kiện Console", "Nâng tầm trải nghiệm game", { x: 933, y: 394, w: 322, h: 215 }),
  ],
  "home-network": [
    st("nw-router", "Bộ phát Wifi", "Bộ Phát Wifi", "Kết nối mạnh mẽ cho mọi không gian", { x: 31, y: 92, w: 467, h: 195 }),
    st("nw-mesh", "Mesh Wifi", "Bộ Mesh Wifi", "Phủ sóng toàn diện cho ngôi nhà thông minh", { x: 507, y: 92, w: 239, h: 372 }),
    st("nw-disk", "Ổ cứng di động", "Ổ cứng di động", "Lưu trữ an toàn, linh hoạt mọi lúc", { x: 754, y: 92, w: 249, h: 195 }),
    st("nw-soft", "Phần mềm", "Phần mềm Bản quyền", "Hiệu suất cao làm việc, học tập và sáng tạo", { x: 1011, y: 92, w: 239, h: 195 }),
    st("nw-switch", "Switch", "Thiết Bị Chia Mạng - Switch", "Giải pháp mạng linh hoạt cho doanh nghiệp và gia đình", { x: 31, y: 295, w: 251, h: 169 }),
    st("nw-ap", "Access Point", "Access Point", "Mở rộng vùng phủ sóng Wifi chuyên nghiệp", { x: 290, y: 295, w: 208, h: 169 }),
    st("nw-usb", "USB", "USB", "Lưu trữ và truyền dữ liệu nhanh chóng", { x: 754, y: 295, w: 241, h: 169 }),
    st("nw-av", "Phần mềm diệt virus", "Bảo Mật & Antivirus", "Bảo vệ thiết bị và dữ liệu an toàn", { x: 1003, y: 295, w: 247, h: 169 }),
  ],
  "home-smart-home": [
    st("sh-hero", "Nhà thông minh", "NHÀ THÔNG MINH", "", { x: 0, y: 0, w: 740, h: 280 }, {
      highlight: "CUỘC SỐNG TIỆN NGHI",
      titleEnd: "AN TOÀN HƠN MỖI NGÀY",
      features: [
        ["shield-check", "An toàn", "Bảo vệ gia đình mọi lúc, mọi nơi"],
        ["house", "Tiện nghi", "Điều khiển dễ dàng, cuộc sống thoải mái"],
        ["leaf", "Tiết kiệm", "Tối ưu điện năng, chi phí hợp lý"],
        ["settings", "Hiện đại", "Công nghệ tiên tiến, kết nối thông minh"],
      ],
    }),
    st("sh-cam", "Camera", "Camera quan sát", "Giám sát mọi lúc, mọi nơi", { x: 35, y: 287, w: 227, h: 147 }, { buttonLabel: "Xem ngay" }),
    st("sh-fire", "Báo cháy", "Báo cháy", "Cảnh báo sớm, bảo vệ an toàn", { x: 272, y: 287, w: 227, h: 147 }, { buttonLabel: "Xem ngay" }),
    st("sh-lock", "Khóa cửa thông minh", "Khóa cửa thông minh", "An tâm cho mọi thành viên", { x: 509, y: 287, w: 220, h: 147 }, { buttonLabel: "Xem ngay" }),
    st("sh-bell", "Chuông cửa có hình", "Chuông cửa có hình", "Nhìn thấy khách, đàm thoại dễ dàng", { x: 35, y: 444, w: 227, h: 154 }, { buttonLabel: "Xem ngay" }),
    st("sh-gimbal", "Gimbal", "GIMBAL cầm tay", "Ghi hình ổn định, bắt trọn khoảnh khắc", { x: 272, y: 444, w: 227, h: 154 }, { buttonLabel: "Xem ngay" }),
    st("sh-power", "Pin dự phòng", "Pin Dự Phòng", "Sạc mọi lúc, luôn sẵn sàng", { x: 509, y: 444, w: 220, h: 154 }, { buttonLabel: "Xem ngay" }),
  ],
};

const SAMPLE_LAYOUT: Record<string, TilesLayout> = {
  "home-gaming-gear": "big-left",
  "home-pc-parts": "big-center",
  "home-console": "tall-left",
  "home-network": "wide-tall",
  "home-smart-home": "banner-top",
};

const heading = (title: string, highlight: string, subtitle: string, allLabel: string, allLink: string) => ({
  title,
  highlight,
  subtitle,
  allLabel,
  allLink,
});

const SAMPLE_HEADING: Record<string, TilesConfig["heading"]> = {
  "home-gaming-gear": heading("GAMING GEAR", "", "", "Xem tất cả Gaming Gear", "/gaming-gear"),
  "home-pc-parts": heading("LINH KIỆN PC", "", "", "Xem tất cả linh kiện PC", "/linh-kien-pc"),
  "home-console": heading("CONSOLE - SIM RACING -", "GIẢI TRÍ", "Thiết bị chơi game chính hãng cho mọi trải nghiệm", "Xem thêm", "/console"),
  "home-network": heading("MẠNG - LƯU TRỮ -", "PHẦN MỀM", "Kết nối ổn định | Lưu trữ an toàn | Phần mềm chính hãng", "Xem thêm", "/mang-luu-tru-phan-mem"),
  // Thiết kế Nhà thông minh không có hàng tiêu đề; tiêu đề nằm trong banner, link nằm ở khối danh mục phụ
  "home-smart-home": heading("NHÀ THÔNG MINH", "", "", "", ""),
};

const SUB_X = [767, 1010];
const SUB_Y = [121, 291, 461];
const SAMPLE_SIDE_NAMES = ["Ổ cắm điện", "Tivi", "Điều hòa", "Máy pha cà phê", "Tủ lạnh", "Máy giặt"];
const sideSampleId = (i: number) => `sh-sub-${i + 1}`;

const SAMPLE_CROPS = new Map<string, Crop>([
  ...Object.values(SAMPLE_TILES)
    .flat()
    .map((t): [string, Crop] => [t.id, t.crop]),
  ...SAMPLE_SIDE_NAMES.map((_, i): [string, Crop] => [
    sideSampleId(i),
    { x: SUB_X[i % 2], y: SUB_Y[Math.floor(i / 2)], w: 239, h: 157 },
  ]),
]);

const emptyTile = (): TileItem => ({
  id: newId(),
  category: { id: null, name: "" },
  title: "",
  highlight: "",
  titleEnd: "",
  description: "",
  link: "",
  desktopImage: null,
  mobileImage: null,
  eyebrow: "",
  features: [],
  buttonLabel: "",
});

const padTiles = (list: TileItem[], count: number) =>
  list.length >= count ? list : [...list, ...Array.from({ length: count - list.length }, emptyTile)];

const initialConfig = (block: HomeBlock): TilesConfig => {
  // Block thêm mới chưa có thiết kế riêng thì dùng lưới đều 4 cột
  const layout = SAMPLE_LAYOUT[block.code] ?? "grid-4";
  const samples = SAMPLE_TILES[block.code] ?? [];
  const isBannerTop = layout === "banner-top";
  return {
    heading: SAMPLE_HEADING[block.code] ?? heading(block.name.toUpperCase(), "", "", "", ""),
    layout,
    tiles: padTiles(
      samples.map((s) => ({
        ...emptyTile(),
        id: s.id,
        // id danh mục chưa biết: tự khớp theo tên khi tải xong cây danh mục
        category: { id: null, name: s.category },
        title: s.title,
        highlight: s.highlight ?? "",
        titleEnd: s.titleEnd ?? "",
        description: s.description,
        eyebrow: s.eyebrow ?? "",
        buttonLabel: s.buttonLabel ?? "",
        features: (s.features ?? []).map(([icon, title, description]) => ({
          id: newId(),
          icon: lucideIcon(icon),
          title,
          description,
        })),
      })),
      layoutOf(layout).cells.length
    ),
    sidePanel:
      isBannerTop && samples.length > 0
        ? { title: "DANH MỤC NỔI BẬT", allLabel: "Xem tất cả danh mục", allLink: "/nha-thong-minh" }
        : { title: "", allLabel: "", allLink: "" },
    sideCategories:
      isBannerTop && samples.length > 0
        ? SAMPLE_SIDE_NAMES.map((name, i) => ({
            id: sideSampleId(i),
            category: { id: null, name },
            image: null,
            title: "",
            link: "",
          }))
        : [],
  };
};

// Sơ đồ bố cục, chỉ để xem: số trong ô là thứ tự ô ở danh sách bên dưới
const LayoutMini = ({ layout }: { layout: LayoutOption }) => {
  const grid = (
    <span
      className={classes.mini}
      style={{
        gridTemplateColumns: layout.columns,
        gridTemplateRows: layout.rows,
        flex: layout.panel ? "740 1 0" : undefined,
      }}
    >
      {layout.cells.map((cell, i) => (
        <span
          key={i}
          className={cell.big ? `${classes.miniCell} ${classes.miniBig}` : classes.miniCell}
          style={cell.style}
        >
          {i + 1}
        </span>
      ))}
    </span>
  );
  if (!layout.panel) return <span aria-hidden="true">{grid}</span>;
  return (
    <span className={classes.miniWrap} aria-hidden="true">
      {grid}
      <span className={classes.miniPanel} style={{ flex: "482 1 0" }}>
        <span className={classes.miniPanelHead} />
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} />
        ))}
      </span>
    </span>
  );
};

type TileRowProps = {
  item: TileItem;
  // Ảnh thiết kế của block, dùng cắt ảnh mẫu
  designId: string;
  cell: LayoutCell;
  index: number;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<TileItem>) => void;
  onFeatures: (update: (list: TileItem["features"]) => TileItem["features"]) => void;
};

const TileRow = ({ item, designId, cell, index, open, onToggle, onChange, onFeatures }: TileRowProps) => {
  const { setNodeRef, style, handleProps } = useSortableItem(item.id);
  const categoryImage = useCategoryImage(item.category.id);
  const sampleCrop = SAMPLE_CROPS.get(item.id);
  const title = [item.title || item.category.name, item.highlight, item.titleEnd]
    .filter(Boolean)
    .join(" ");
  const filled = !!title || !!item.desktopImage;
  const slot = slotLabel(cell, index);

  return (
    <div ref={setNodeRef} className={`${classes.tile} ${open ? classes.tileOpen : ""}`} style={style}>
      <Group gap={12} wrap="nowrap" px={10} py={8}>
        <DragHandle label={`Kéo để đổi vị trí ${slot}`} handleProps={handleProps} size={16} />
        {/* Ảnh máy tính của ô: nằm ngoài nút mở/đóng để bấm vào là chọn ảnh */}
        <ThumbPicker
          label={slot}
          src={item.desktopImage}
          fallbackSrc={categoryImage}
          fallbackNote="Ảnh của danh mục"
          imageId={designId}
          crop={sampleCrop}
          onPick={(url) => onChange({ desktopImage: url })}
          onReset={() => onChange({ desktopImage: null })}
          resetLabel="Dùng lại ảnh của danh mục"
          width={64}
          height={48}
        />
        <button
          type="button"
          className={classes.tileHead}
          aria-expanded={open}
          onClick={onToggle}
          style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}
        >
          <Stack gap={3} miw={0}>
            <Text fz={14} fw={600} lh={1.3} truncate>
              {filled ? title || "Chưa có tiêu đề" : "Ô trống"}
            </Text>
            <Text fz={12} lh={1.3} truncate className={`${classes.mono} ${classes.muted}`}>
              {item.link ||
                (item.category.id !== null
                  ? `Trang danh mục ${item.category.name}`
                  : item.category.name
                    ? `Chưa khớp danh mục: ${item.category.name}`
                    : "Chưa chọn danh mục")}
            </Text>
          </Stack>
          <Badge variant="light" color={cell.big ? "blue" : "gray"} radius="xl" tt="none" fw={600}>
            {slot}
          </Badge>
        </button>
      </Group>
      {open && (
        <Stack gap={12} pt={4} pb={14} pr={12} pl={42}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <CategoryTreeSelect
              label="Danh mục"
              value={item.category.id}
              fallbackName={item.category.name}
              onChange={(id, node) =>
                onChange({ category: node ? { id: node.id, name: node.name } : { id, name: "" } })
              }
            />
            <TextInput
              label="Link"
              placeholder="Mặc định: trang của danh mục"
              value={item.link}
              onChange={(e) => onChange({ link: e.currentTarget.value })}
            />
          </SimpleGrid>
          <Group gap={20} align="flex-start">
            <Box>
              <Text fz={14} fw={500} mb={6}>
                Ảnh máy tính
              </Text>
              <ThumbPicker
                label={slot}
                src={item.desktopImage}
                fallbackSrc={categoryImage}
                fallbackNote="Ảnh của danh mục"
                imageId={designId}
                crop={sampleCrop}
                onPick={(url) => onChange({ desktopImage: url })}
                onReset={() => onChange({ desktopImage: null })}
                resetLabel="Dùng lại ảnh của danh mục"
                width={96}
              />
            </Box>
            <Box>
              <Text fz={14} fw={500} mb={6}>
                Ảnh điện thoại
              </Text>
              <ThumbPicker
                label={`${slot} trên điện thoại`}
                src={item.mobileImage}
                onPick={(url) => onChange({ mobileImage: url })}
                onReset={() => onChange({ mobileImage: null })}
                resetLabel="Bỏ ảnh điện thoại"
                width={96}
              />
            </Box>
            <Text fz={12} maw={280} mt={26} className={classes.muted}>
              Chưa có ảnh máy tính thì dùng ảnh của danh mục; chưa có ảnh điện thoại thì thu nhỏ ảnh
              máy tính.
            </Text>
          </Group>
          {cell.big && (
            <TextInput
              label="Nhãn nhỏ"
              value={item.eyebrow}
              onChange={(e) => onChange({ eyebrow: e.currentTarget.value })}
            />
          )}
          {cell.big ? (
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={12}>
              <TextInput
                label="Tiêu đề"
                placeholder={item.category.name || "Tên danh mục"}
                value={item.title}
                onChange={(e) => onChange({ title: e.currentTarget.value })}
              />
              <TextInput
                label="Phần tô màu"
                description="Nối sau tiêu đề."
                inputWrapperOrder={["label", "input", "description"]}
                value={item.highlight}
                onChange={(e) => onChange({ highlight: e.currentTarget.value })}
              />
              <TextInput
                label="Phần cuối tiêu đề"
                description="Nối sau phần tô màu, không tô."
                inputWrapperOrder={["label", "input", "description"]}
                value={item.titleEnd}
                onChange={(e) => onChange({ titleEnd: e.currentTarget.value })}
              />
            </SimpleGrid>
          ) : (
            <TextInput
              label="Tiêu đề"
              placeholder={item.category.name || "Tên danh mục"}
              description="Để trống thì dùng tên danh mục."
              inputWrapperOrder={["label", "input", "description"]}
              value={item.title}
              onChange={(e) => onChange({ title: e.currentTarget.value })}
            />
          )}
          <Textarea
            label="Mô tả"
            autosize
            minRows={2}
            maxRows={3}
            value={item.description}
            onChange={(e) => onChange({ description: e.currentTarget.value })}
            description="Tối đa 2 dòng trên web, xuống dòng bằng Enter."
            inputWrapperOrder={["label", "input", "description"]}
          />
          {cell.big && (
            <Box>
              <Text fz={14} fw={500} mb={6}>
                Điểm nổi bật (tối đa 4)
              </Text>
              <EditableList
                items={item.features}
                onChange={(action) =>
                  onFeatures((list) => (typeof action === "function" ? action(list) : action))
                }
                itemLabel={(f) => f.title}
                columns={[
                  { label: "Icon", width: "170px" },
                  { label: "Tiêu đề", width: "minmax(0, 1fr)" },
                  { label: "Mô tả", width: "minmax(0, 1fr)" },
                ]}
                renderCells={(f, update) => (
                  <>
                    <IconPicker
                      ariaLabel={`Icon ${f.title}`}
                      value={f.icon}
                      onChange={(value) => update({ icon: value })}
                    />
                    <TextInput
                      aria-label="Tiêu đề điểm nổi bật"
                      value={f.title}
                      onChange={(e) => update({ title: e.currentTarget.value })}
                    />
                    <TextInput
                      aria-label={`Mô tả ${f.title}`}
                      value={f.description}
                      onChange={(e) => update({ description: e.currentTarget.value })}
                    />
                  </>
                )}
                newItem={() => ({ id: newId(), icon: null, title: "", description: "" })}
                addLabel="Thêm điểm nổi bật"
                max={4}
                minWidth={520}
              />
            </Box>
          )}
          <TextInput
            label="Nhãn nút"
            placeholder="Để trống thì chỉ hiện nút mũi tên"
            value={item.buttonLabel}
            onChange={(e) => onChange({ buttonLabel: e.currentTarget.value })}
          />
        </Stack>
      )}
    </div>
  );
};

// Ô ảnh của danh mục phụ: mặc định ảnh của danh mục, cho tải ảnh khác
const SideImageCell = ({
  item,
  imageId,
  update,
}: {
  item: TileSideCategory;
  imageId: string;
  update: (patch: Partial<TileSideCategory>) => void;
}) => {
  const categoryImage = useCategoryImage(item.category.id);
  return (
    <ThumbPicker
      label={item.title || item.category.name || "danh mục phụ"}
      src={item.image}
      fallbackSrc={categoryImage}
      fallbackNote="Ảnh của danh mục"
      imageId={imageId}
      crop={SAMPLE_CROPS.get(item.id)}
      onPick={(url) => update({ image: url })}
      onReset={() => update({ image: null })}
      resetLabel="Dùng lại ảnh của danh mục"
      width={72}
    />
  );
};

const EditTiles = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading: head, tiles, sidePanel, sideCategories } = config;
  const current = layoutOf(config.layout);
  const shown = tiles.slice(0, current.cells.length);
  const [openId, setOpenId] = useState<string | null>(() => shown[0]?.id ?? null);
  const filledCount = shown.filter((t) => t.title || t.category.name || t.desktopImage).length;
  const { nodes: categories, loading: categoriesLoading } = useCategoryTree();
  const matchedRef = useRef(false);

  // Ô đang đóng không hiện ô chọn danh mục, nên khớp tên danh mục mẫu với cây danh mục ở đây
  useEffect(() => {
    if (matchedRef.current || categoriesLoading || categories.length === 0) return;
    matchedRef.current = true;
    setConfig((c) => ({
      ...c,
      tiles: c.tiles.map((t) => {
        if (t.category.id !== null || !t.category.name) return t;
        const found = matchCategory(categories, t.category.name);
        return found ? { ...t, category: { id: found.id, name: found.name } } : t;
      }),
    }));
  }, [categories, categoriesLoading, setConfig]);

  const patchHeading = (v: Partial<TilesConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const patchPanel = (v: Partial<TilesConfig["sidePanel"]>) =>
    setConfig((c) => ({ ...c, sidePanel: { ...c.sidePanel, ...v } }));
  const patchTile = (id: string, update: (t: TileItem) => TileItem) =>
    setConfig((c) => ({ ...c, tiles: c.tiles.map((t) => (t.id === id ? update(t) : t)) }));

  return (
    <Flex wrap="wrap" align="flex-start" gap={16}>
      <Stack gap={16} style={{ flex: "999 1 640px", minWidth: 0 }}>
        <SectionCard title="Thông tin chung">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Tiêu đề block"
              value={head.title}
              onChange={(e) => patchHeading({ title: e.currentTarget.value })}
            />
            <TextInput
              label="Phần tô màu trong tiêu đề"
              description="Hiện màu đỏ, nối sau tiêu đề."
              inputWrapperOrder={["label", "input", "description"]}
              value={head.highlight}
              onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
            />
          </SimpleGrid>
          <TextInput
            label="Dòng phụ dưới tiêu đề"
            value={head.subtitle}
            onChange={(e) => patchHeading({ subtitle: e.currentTarget.value })}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Nhãn Xem tất cả"
              description="Để trống thì ẩn nút."
              inputWrapperOrder={["label", "input", "description"]}
              value={head.allLabel}
              onChange={(e) => patchHeading({ allLabel: e.currentTarget.value })}
            />
            <TextInput
              label="Link"
              value={head.allLink}
              onChange={(e) => patchHeading({ allLink: e.currentTarget.value })}
            />
          </SimpleGrid>
        </SectionCard>

        <SectionCard
          title="Các ô"
          bodyGap={8}
          right={
            <Text fz={13} className={classes.muted}>
              {filledCount} / {current.cells.length} ô đã có nội dung
            </Text>
          }
        >
          <Group gap={14} wrap="wrap" align="center" mb={4}>
            <Box w={260} miw={200}>
              <LayoutMini layout={current} />
            </Box>
            <Box miw={0} style={{ flex: "1 1 220px" }}>
              <Text fz={13} fw={600}>
                {current.name}
              </Text>
              <Text fz={12} className={classes.muted}>
                Bố cục cố định theo thiết kế của block. Số trong sơ đồ là thứ tự ô bên dưới, kéo
                thả để đổi chỗ ô.
              </Text>
            </Box>
          </Group>
          <SortableArea
            ids={shown.map((t) => t.id)}
            onReorder={(from, to) =>
              setConfig((c) => ({
                ...c,
                tiles: [
                  ...reorderList(c.tiles.slice(0, current.cells.length), from, to),
                  ...c.tiles.slice(current.cells.length),
                ],
              }))
            }
          >
            {shown.map((t, i) => (
              <TileRow
                key={t.id}
                item={t}
                designId={block.thumbId}
                cell={current.cells[i]}
                index={i}
                open={openId === t.id}
                onToggle={() => setOpenId(openId === t.id ? null : t.id)}
                onChange={(patch) => patchTile(t.id, (x) => ({ ...x, ...patch }))}
                onFeatures={(update) => patchTile(t.id, (x) => ({ ...x, features: update(x.features) }))}
              />
            ))}
          </SortableArea>
        </SectionCard>

        {current.panel && (
          <SectionCard
            title="Khối danh mục phụ bên phải"
            right={
              <Text fz={13} className={classes.muted}>
                Ảnh lấy từ danh mục, bấm ảnh để đổi
              </Text>
            }
          >
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={12}>
              <TextInput
                label="Tiêu đề khối"
                value={sidePanel.title}
                onChange={(e) => patchPanel({ title: e.currentTarget.value })}
              />
              <TextInput
                label="Nhãn link"
                description="Để trống thì ẩn link."
                inputWrapperOrder={["label", "input", "description"]}
                value={sidePanel.allLabel}
                onChange={(e) => patchPanel({ allLabel: e.currentTarget.value })}
              />
              <TextInput
                label="Link"
                value={sidePanel.allLink}
                onChange={(e) => patchPanel({ allLink: e.currentTarget.value })}
              />
            </SimpleGrid>
            <EditableList
              items={sideCategories}
              onChange={fieldSetter(setConfig, "sideCategories")}
              itemLabel={(s) => s.title || s.category.name}
              columns={[
                { label: "Ảnh", width: "72px" },
                { label: "Danh mục", width: "minmax(0, 1fr)" },
                { label: "Tên hiển thị", width: "minmax(0, 1fr)" },
                { label: "Link", width: "minmax(0, 1fr)" },
              ]}
              renderCells={(s, update) => (
                <>
                  <SideImageCell item={s} imageId={block.thumbId} update={update} />
                  <CategoryTreeSelect
                    ariaLabel="Danh mục"
                    value={s.category.id}
                    fallbackName={s.category.name}
                    onChange={(id, node) =>
                      update({ category: node ? { id: node.id, name: node.name } : { id, name: "" } })
                    }
                  />
                  <TextInput
                    aria-label="Tên hiển thị"
                    placeholder={s.category.name || "Tên danh mục"}
                    value={s.title}
                    onChange={(e) => update({ title: e.currentTarget.value })}
                  />
                  <TextInput
                    aria-label={`Link ${s.title || s.category.name}`}
                    placeholder="Mặc định: trang danh mục"
                    value={s.link}
                    onChange={(e) => update({ link: e.currentTarget.value })}
                  />
                </>
              )}
              newItem={() => ({
                id: newId(),
                category: { id: null, name: "" },
                image: null,
                title: "",
                link: "",
              })}
              addLabel="Thêm danh mục"
              max={6}
              minWidth={720}
            />
          </SectionCard>
        )}
      </Stack>

      <Box
        component="aside"
        aria-label="Ảnh thiết kế"
        className={classes.detailSticky}
        style={{ flex: "1 1 380px", maxWidth: "100%" }}
      >
        <Paper withBorder radius="md">
          <Text
            component="h2"
            fw={700}
            fz={15}
            m={0}
            px={16}
            py={14}
            style={{ borderBottom: "1px solid var(--hb-border)" }}
          >
            Ảnh thiết kế
          </Text>
          <Box p={16} bg="var(--hb-surface-2)">
            <ZoomableImage imageId={block.thumbId} alt={`Ảnh thiết kế ${block.name}`} />
          </Box>
          <Text fz={12} px={16} pt={10} pb={14} className={classes.muted}>
            Ảnh thiết kế gốc để đối chiếu, không đổi theo nội dung đang sửa.
          </Text>
        </Paper>
      </Box>
    </Flex>
  );
};

export default EditTiles;
